import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../i18n/I18nContext';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { toast, ToastContainer } from 'react-toastify';
import {
    Plus, Users, Inbox, Filter, Building2, Mail, Phone, MapPin, Send, Clock, Check, X, ShieldCheck, Search, UserPlus, Globe
} from 'lucide-react';
import {
    getMySubcontractors,
    searchSubcontractors,
    getReceivedInvitations,
    getSentInvitations,
    addSubcontractor,
    acceptInvitation,
    rejectInvitation,
    createSubcontractor
} from '../../services/missionService';

const Subcontractors = () => {
    const { t, locale } = useI18n();
    const [subcontractors, setSubcontractors] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const navigate = useNavigate();

    const [filter, setFilter] = useState('ALL'); // ALL, TMS_ACCOUNT, MANUAL
    const [activeTab, setActiveTab] = useState('subcontractors'); // subcontractors, invitations
    const [invitationsTab, setInvitationsTab] = useState('received'); // received, sent
    const [receivedInvitations, setReceivedInvitations] = useState([]);
    const [sentInvitations, setSentInvitations] = useState([]);

    // Search TMS state
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);

    // Manual Creation state
    const [newSubForm, setNewSubForm] = useState({
        enterpriseName: '',
        contactEmail: '',
        telephone: '',
        country: '',
        city: '',
        truckTypes: '',
        operatingZones: '',
        cargoTypes: '',
        subcontractorType: 'MANUAL'
    });

    useEffect(() => {
        loadSubcontractors();
        loadInvitations();
    }, []);

    const loadSubcontractors = async () => {
        try {
            setLoading(true);
            const data = await getMySubcontractors();
            setSubcontractors(data);
        } catch (err) {
            toast.error(t('subcontracting.toast.loadError'));
        } finally {
            setLoading(false);
        }
    };

    const handleSearchTMS = async () => {
        if (searchQuery.length < 2) return;
        try {
            setSearching(true);
            const data = await searchSubcontractors(searchQuery);
            setSearchResults(data);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setSearching(false);
        }
    };

    const loadInvitations = async () => {
        try {
            const [received, sent] = await Promise.all([
                getReceivedInvitations(),
                getSentInvitations()
            ]);
            setReceivedInvitations(received);
            setSentInvitations(sent);
        } catch (err) {
            console.error('Failed to load invitations:', err);
        }
    };

    const handleAddCollaborator = async (subId) => {
        try {
            await addSubcontractor(subId);
            toast.success(t('subcontracting.toast.invitationSent'));
            loadInvitations();
            setSearchResults([]);
            setSearchQuery('');
        } catch (err) {
            toast.error(err.message);
        }
    };

    const handleAcceptInvitation = async (invitationId) => {
        try {
            await acceptInvitation(invitationId);
            toast.success(t('subcontracting.toast.invitationAccepted'));
            loadInvitations();
            loadSubcontractors();
        } catch (err) {
            toast.error(err.message);
        }
    };

    const handleRejectInvitation = async (invitationId) => {
        try {
            await rejectInvitation(invitationId);
            toast.success(t('subcontracting.toast.invitationRefused'));
            loadInvitations();
        } catch (err) {
            toast.error(err.message);
        }
    };

    const handleCreateManual = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);
            const toList = (s) => s.split(',').map(x => x.trim()).filter(Boolean);
            const payload = {
                ...newSubForm,
                truckTypes: toList(newSubForm.truckTypes),
                operatingZones: toList(newSubForm.operatingZones),
                cargoTypes: toList(newSubForm.cargoTypes)
            };
            await createSubcontractor(payload);
            toast.success(t('subcontracting.toast.manualCreated'));
            setShowCreateModal(false);
            loadSubcontractors();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    const filteredSubs = subcontractors.filter(s => {
        if (filter === 'ALL') return true;
        return s.subcontractorType === filter;
    });

    return (
        <DashboardLayout>
            <ToastContainer />
            <div className="container py-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                        <h2 className="fw-bold mb-1">{t('subcontracting.title')}</h2>
                        <p className="text-muted mb-0">{t('subcontracting.subtitle')}</p>
                    </div>
                    <button
                        className="btn btn-primary d-flex align-items-center gap-2 px-4 shadow-sm"
                        onClick={() => setShowCreateModal(true)}
                    >
                        <Plus size={20} />
                        {t('subcontracting.addProvider')}
                    </button>
                </div>

                {/* Tabs */}
                <div className="card shadow-sm border-0 rounded-4 mb-4">
                    <div className="card-body p-3">
                        <div className="btn-group w-100" role="group">
                            <button
                                className={`btn ${activeTab === 'subcontractors' ? 'btn-primary' : 'btn-outline-primary'}`}
                                onClick={() => setActiveTab('subcontractors')}
                            >
                                <Users size={18} className="me-2" />
                                {t('subcontracting.tabs.subcontractors')} ({subcontractors.length})
                            </button>
                            <button
                                className={`btn ${activeTab === 'invitations' ? 'btn-primary' : 'btn-outline-primary'} position-relative`}
                                onClick={() => setActiveTab('invitations')}
                            >
                                <Inbox size={18} className="me-2" />
                                {t('subcontracting.tabs.requests')}
                                {receivedInvitations.filter(i => i.status === 'PENDING').length > 0 && (
                                    <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
                                        {receivedInvitations.filter(i => i.status === 'PENDING').length}
                                    </span>
                                )}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Subcontractors Tab */}
                {activeTab === 'subcontractors' && (
                    <>
                        {/* Filters */}
                        <div className="card shadow-sm border-0 rounded-4 mb-4">
                            <div className="card-body p-3">
                                <div className="d-flex align-items-center gap-3">
                                    <span className="text-muted small fw-bold"><Filter size={16} className="me-1" /> {t('subcontracting.filter.label')}</span>
                                    <div className="btn-group btn-group-sm rounded-3 overflow-hidden shadow-none border">
                                        <button
                                            className={`btn ${filter === 'ALL' ? 'btn-primary' : 'btn-white border-0 text-muted'}`}
                                            onClick={() => setFilter('ALL')}
                                        >
                                            {t('subcontracting.filter.all')}
                                        </button>
                                        <button
                                            className={`btn ${filter === 'TMS_ACCOUNT' ? 'btn-primary' : 'btn-white border-0 text-muted'}`}
                                            onClick={() => setFilter('TMS_ACCOUNT')}
                                        >
                                            {t('subcontracting.filter.tms')}
                                        </button>
                                        <button
                                            className={`btn ${filter === 'MANUAL' ? 'btn-primary' : 'btn-white border-0 text-muted'}`}
                                            onClick={() => setFilter('MANUAL')}
                                        >
                                            {t('subcontracting.filter.manual')}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {loading ? (
                            <div className="text-center p-5"><span className="spinner-border" /></div>
                        ) : (
                            <div className="row g-4">
                                {filteredSubs.length > 0 ? (
                                    filteredSubs.map(s => (
                                        <div key={s.id} className="col-12 col-md-6 col-lg-4">
                                            <div
                                                className="card h-100 shadow-sm border-0 rounded-4 hover-lift transition-all cursor-pointer"
                                                onClick={() => navigate(`/subcontractors/${s.id}`)}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <div className="card-body p-4">
                                                    <div className="d-flex justify-content-between align-items-start mb-3">
                                                        <div className="bg-light p-3 rounded-4 text-primary">
                                                            <Building2 size={24} />
                                                        </div>
                                                        <span className={`badge rounded-pill ${s.subcontractorType === 'TMS_ACCOUNT' ? 'bg-info bg-opacity-10 text-info' : 'bg-secondary bg-opacity-10 text-secondary'}`}>
                                                            {s.subcontractorType === 'TMS_ACCOUNT' ? t('subcontracting.card.tmsAccount') : t('subcontracting.card.manual')}
                                                        </span>
                                                    </div>
                                                    <h5 className="fw-bold mb-1">{s.enterpriseName}</h5>
                                                    <div className="vstack gap-2 mt-4 small">
                                                        <div className="d-flex align-items-center gap-2 text-muted">
                                                            <Mail size={14} /> {s.contactEmail || s.email}
                                                        </div>
                                                        <div className="d-flex align-items-center gap-2 text-muted">
                                                            <Phone size={14} /> {s.telephone || t('common.nc')}
                                                        </div>
                                                        <div className="d-flex align-items-center gap-2 text-muted">
                                                            <MapPin size={14} /> {[s.city, s.country].filter(Boolean).join(', ') || t('common.nc')}
                                                        </div>
                                                    </div>

                                                    <div className="mt-4 pt-3 border-top">
                                                        <div className="d-flex gap-2">
                                                            {s.truckTypes?.length > 0 && (
                                                                <span className="badge bg-light text-dark fw-normal rounded-pill px-3">{s.truckTypes[0]}</span>
                                                            )}
                                                            {s.cargoTypes?.length > 0 && (
                                                                <span className="badge bg-light text-dark fw-normal rounded-pill px-3">{s.cargoTypes[0]}</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="col-12 text-center py-5">
                                        <div className="text-muted mb-3"><Users size={48} className="opacity-25" /></div>
                                        <h5 className="text-muted">{t('subcontracting.empty.noSubFound')}</h5>
                                        <p className="text-muted small">{t('subcontracting.empty.addFirst')}</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}

                {/* Invitations Tab */}
                {activeTab === 'invitations' && (
                    <>
                        <div className="card shadow-sm border-0 rounded-4 mb-4">
                            <div className="card-body p-3">
                                <div className="btn-group w-100" role="group">
                                    <button
                                        className={`btn ${invitationsTab === 'received' ? 'btn-primary' : 'btn-outline-primary'}`}
                                        onClick={() => setInvitationsTab('received')}
                                    >
                                        <Inbox size={18} className="me-2" />
                                        {t('subcontracting.invitations.received')} ({receivedInvitations.length})
                                    </button>
                                    <button
                                        className={`btn ${invitationsTab === 'sent' ? 'btn-primary' : 'btn-outline-primary'}`}
                                        onClick={() => setInvitationsTab('sent')}
                                    >
                                        <Send size={18} className="me-2" />
                                        {t('subcontracting.invitations.sent')} ({sentInvitations.length})
                                    </button>
                                </div>
                            </div>
                        </div>

                        {invitationsTab === 'received' && (
                            <div className="vstack gap-3">
                                {receivedInvitations.length > 0 ? (
                                    receivedInvitations.map(inv => (
                                        <div key={inv.id} className="card shadow-sm border-0 rounded-4">
                                            <div className="card-body p-4">
                                                <div className="d-flex justify-content-between align-items-start">
                                                    <div className="flex-grow-1">
                                                        <div className="d-flex align-items-center gap-3 mb-2">
                                                            <div className="bg-light p-2 rounded-3">
                                                                <Building2 size={20} className="text-primary" />
                                                            </div>
                                                            <div>
                                                                <h6 className="fw-bold mb-0">{inv.senderEnterpriseName}</h6>
                                                                <small className="text-muted">{inv.senderCity}, {inv.senderCountry}</small>
                                                            </div>
                                                        </div>
                                                        {inv.message && (
                                                            <p className="text-muted small mb-2 ms-5">{inv.message}</p>
                                                        )}
                                                        <small className="text-muted ms-5">
                                                            <Clock size={12} className="me-1" />
                                                            {new Date(inv.createdAt).toLocaleDateString(locale === 'en' ? 'en-US' : 'fr-FR')}
                                                        </small>
                                                    </div>
                                                    <div className="d-flex flex-column gap-2">
                                                        {inv.status === 'PENDING' ? (
                                                            <>
                                                                <button
                                                                    className="btn btn-sm btn-success d-flex align-items-center gap-1"
                                                                    onClick={() => handleAcceptInvitation(inv.id)}
                                                                >
                                                                    <Check size={16} /> {t('subcontracting.invitations.accept')}
                                                                </button>
                                                                <button
                                                                    className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1"
                                                                    onClick={() => handleRejectInvitation(inv.id)}
                                                                >
                                                                    <X size={16} /> {t('subcontracting.invitations.refuse')}
                                                                </button>
                                                            </>
                                                        ) : (
                                                            <span className={`badge ${inv.status === 'ACCEPTED' ? 'bg-success' : 'bg-danger'}`}>
                                                                {inv.status === 'ACCEPTED' ? t('subcontracting.invitations.accepted') : t('subcontracting.invitations.refused')}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="text-center py-5">
                                        <Inbox size={48} className="text-muted opacity-25 mb-3" />
                                        <h5 className="text-muted">{t('subcontracting.invitations.noReceived')}</h5>
                                    </div>
                                )}
                            </div>
                        )}

                        {invitationsTab === 'sent' && (
                            <div className="vstack gap-3">
                                {sentInvitations.length > 0 ? (
                                    sentInvitations.map(inv => (
                                        <div key={inv.id} className="card shadow-sm border-0 rounded-4">
                                            <div className="card-body p-4">
                                                <div className="d-flex justify-content-between align-items-start">
                                                    <div className="flex-grow-1">
                                                        <div className="d-flex align-items-center gap-3 mb-2">
                                                            <div className="bg-light p-2 rounded-3">
                                                                <Building2 size={20} className="text-primary" />
                                                            </div>
                                                            <div>
                                                                <h6 className="fw-bold mb-0">{inv.recipientEnterpriseName}</h6>
                                                                <small className="text-muted">{inv.recipientCity}, {inv.recipientCountry}</small>
                                                            </div>
                                                        </div>
                                                        <small className="text-muted ms-5">
                                                            <Clock size={12} className="me-1" />
                                                            {new Date(inv.createdAt).toLocaleDateString(locale === 'en' ? 'en-US' : 'fr-FR')}
                                                        </small>
                                                    </div>
                                                    <span className={`badge ${inv.status === 'PENDING' ? 'bg-warning' :
                                                        inv.status === 'ACCEPTED' ? 'bg-success' : 'bg-danger'
                                                        }`}>
                                                        {inv.status === 'PENDING' ? t('subcontracting.invitations.pending') :
                                                            inv.status === 'ACCEPTED' ? t('subcontracting.invitations.accepted') : t('subcontracting.invitations.refused')}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="text-center py-5">
                                        <Send size={48} className="text-muted opacity-25 mb-3" />
                                        <h5 className="text-muted">{t('subcontracting.invitations.noSent')}</h5>
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* ADD PRESTATAIRE MODAL */}
            {showCreateModal && (
                <div className="modal d-block" style={{ backgroundColor: 'rgba(15, 23, 42, 0.7)', backdropFilter: 'blur(8px)', zIndex: 1050 }}>
                    <div className="modal-dialog modal-xl modal-dialog-centered">
                        <div className="modal-content border-0 rounded-5 shadow-2xl overflow-hidden" style={{ animation: 'modalFadeIn 0.3s ease-out' }}>
                            {/* Premium Header */}
                            <div className="modal-header border-0 p-4 pb-0 d-flex justify-content-between align-items-center">
                                <div>
                                    <h3 className="modal-title fw-800 text-dark mb-1">{t('subcontracting.modal.addTitle')}</h3>
                                    <div className="d-flex align-items-center gap-2">
                                        <span className="badge bg-primary bg-opacity-10 text-primary px-3 py-2 rounded-pill small fw-bold">
                                            {t('subcontracting.title')}
                                        </span>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className="btn-close bg-light rounded-circle p-3 shadow-sm hover-rotate transition-all"
                                    onClick={() => setShowCreateModal(false)}
                                    aria-label="Close"
                                ></button>
                            </div>

                            <div className="modal-body p-4 p-lg-5 pt-lg-2">
                                <div className="row g-5 mt-0">
                                    {/* Left: Search TMS */}
                                    <div className="col-12 col-lg-6 position-relative">
                                        <div className="p-4 rounded-4 h-100" style={{ background: 'rgba(52, 152, 219, 0.03)', border: '1px dashed rgba(52, 152, 219, 0.2)' }}>
                                            <div className="d-flex align-items-center gap-3 mb-4">
                                                <div className="bg-primary shadow-sm p-3 rounded-4 text-white">
                                                    <ShieldCheck size={24} />
                                                </div>
                                                <div>
                                                    <h5 className="fw-bold mb-0 text-dark">{t('subcontracting.modal.searchTms')}</h5>
                                                    <p className="small text-muted mb-0">{t('subcontracting.modal.searchSubtitle')}</p>
                                                </div>
                                            </div>

                                            <div className="position-relative mb-4">
                                                <div className="input-group input-group-lg shadow-sm border rounded-4 overflow-hidden bg-white group-focus-within transition-all">
                                                    <span className="input-group-text border-0 bg-transparent ps-3 text-muted">
                                                        <Search size={20} />
                                                    </span>
                                                    <input
                                                        type="text"
                                                        className="form-control border-0 bg-transparent py-3 ps-2 shadow-none"
                                                        placeholder={t('subcontracting.modal.searchPlaceholder')}
                                                        value={searchQuery}
                                                        onChange={(e) => setSearchQuery(e.target.value)}
                                                        onKeyPress={(e) => e.key === 'Enter' && handleSearchTMS()}
                                                    />
                                                    <button
                                                        className="btn btn-primary px-4 fw-bold"
                                                        onClick={handleSearchTMS}
                                                        disabled={searching}
                                                    >
                                                        {searching ? <span className="spinner-border spinner-border-sm" /> : t('common.search')}
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="vstack gap-3 overflow-auto pe-2 custom-scrollbar" style={{ maxHeight: '20rem', minHeight: '12rem' }}>
                                                {searchResults.length > 0 ? (
                                                    searchResults.map(s => (
                                                        <div key={s.id} className="p-4 bg-white border rounded-4 d-flex justify-content-between align-items-center hover-shadow transition-all group">
                                                            <div className="d-flex align-items-center gap-3">
                                                                <div className="bg-light p-2 rounded-3 text-primary group-hover-bg-primary transition-all">
                                                                    <Building2 size={20} />
                                                                </div>
                                                                <div>
                                                                    <div className="fw-bold text-dark">{s.enterpriseName}</div>
                                                                    <div className="small text-muted d-flex align-items-center gap-1">
                                                                        <MapPin size={12} /> {s.city}, {s.country}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <button
                                                                className="btn btn-sm btn-outline-primary rounded-pill px-4 py-2 d-flex align-items-center gap-2 fw-bold"
                                                                onClick={() => handleAddCollaborator(s.id)}
                                                            >
                                                                <UserPlus size={16} /> {t('common.add')}
                                                            </button>
                                                        </div>
                                                    ))
                                                ) : searching ? (
                                                    <div className="text-center py-5">
                                                        <div className="spinner-border text-primary opacity-50 mb-3" />
                                                        <div className="text-muted small">{t('subcontracting.modal.searching')}</div>
                                                    </div>
                                                ) : searchQuery.length > 0 ? (
                                                    <div className="text-center py-5 bg-white border border-dashed rounded-4">
                                                        <div className="text-muted opacity-25 mb-2"><Search size={40} /></div>
                                                        <div className="fw-bold text-muted">{t('subcontracting.modal.noResults')}</div>
                                                        <div className="small text-muted opacity-75">{t('subcontracting.modal.noResultsSub')}</div>
                                                    </div>
                                                ) : (
                                                    <div className="text-center py-5 h-100 d-flex flex-column justify-content-center opacity-50">
                                                        <div className="mb-2"><Users size={32} /></div>
                                                        <div className="small text-muted">{t('subcontracting.modal.searchPrompt')}</div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        {/* Divider for desktop */}
                                        <div className="d-none d-lg-block position-absolute top-50 start-100 translate-middle h-75 border-start opacity-25" style={{ zIndex: 1, left: 'calc(100% + 1.5rem)' }}></div>
                                    </div>

                                    {/* Right: Create Manual */}
                                    <div className="col-12 col-lg-6">
                                        <div className="p-4 rounded-4" style={{ background: 'rgba(25, 135, 84, 0.03)', border: '1px dashed rgba(25, 135, 84, 0.2)' }}>
                                            <div className="d-flex align-items-center gap-3 mb-4">
                                                <div className="bg-success shadow-sm p-3 rounded-4 text-white">
                                                    <Plus size={24} />
                                                </div>
                                                <div>
                                                    <h5 className="fw-bold mb-0 text-dark">{t('subcontracting.modal.addManual')}</h5>
                                                    <p className="small text-muted mb-0">{t('subcontracting.modal.manualSubtitle')}</p>
                                                </div>
                                            </div>

                                            <form onSubmit={handleCreateManual} className="row g-4">
                                                <div className="col-12">
                                                    <div className="form-group-modern">
                                                        <label className="label-modern mb-2">
                                                            <Building2 size={14} className="me-1 opacity-75" />
                                                            {t('subcontracting.modal.enterpriseName')}
                                                        </label>
                                                        <input
                                                            required
                                                            className="form-control-modern p-3 rounded-4 w-100"
                                                            placeholder={t('subcontracting.modal.enterprisePlaceholder')}
                                                            value={newSubForm.enterpriseName}
                                                            onChange={e => setNewSubForm({ ...newSubForm, enterpriseName: e.target.value })}
                                                        />
                                                    </div>
                                                </div>

                                                <div className="col-md-6">
                                                    <div className="form-group-modern">
                                                        <label className="label-modern mb-2">
                                                            <Mail size={14} className="me-1 opacity-75" />
                                                            {t('subcontracting.modal.contactEmail')}
                                                        </label>
                                                        <input
                                                            required
                                                            type="email"
                                                            className="form-control-modern p-3 rounded-4 w-100"
                                                            placeholder={t('subcontracting.modal.emailPlaceholder')}
                                                            value={newSubForm.contactEmail}
                                                            onChange={e => setNewSubForm({ ...newSubForm, contactEmail: e.target.value })}
                                                        />
                                                    </div>
                                                </div>

                                                <div className="col-md-6">
                                                    <div className="form-group-modern">
                                                        <label className="label-modern mb-2">
                                                            <Phone size={14} className="me-1 opacity-75" />
                                                            {t('subcontracting.modal.phone')}
                                                        </label>
                                                        <input
                                                            className="form-control-modern p-3 rounded-4 w-100"
                                                            placeholder={t('subcontracting.modal.phonePlaceholder')}
                                                            value={newSubForm.telephone}
                                                            onChange={e => setNewSubForm({ ...newSubForm, telephone: e.target.value })}
                                                        />
                                                    </div>
                                                </div>

                                                <div className="col-md-6">
                                                    <div className="form-group-modern">
                                                        <label className="label-modern mb-2">
                                                            <Globe size={14} className="me-1 opacity-75" />
                                                            {t('subcontracting.modal.country')}
                                                        </label>
                                                        <input
                                                            className="form-control-modern p-3 rounded-4 w-100"
                                                            placeholder={t('subcontracting.modal.countryPlaceholder')}
                                                            value={newSubForm.country}
                                                            onChange={e => setNewSubForm({ ...newSubForm, country: e.target.value })}
                                                        />
                                                    </div>
                                                </div>

                                                <div className="col-md-6">
                                                    <div className="form-group-modern">
                                                        <label className="label-modern mb-2">
                                                            <MapPin size={14} className="me-1 opacity-75" />
                                                            {t('subcontracting.modal.city')}
                                                        </label>
                                                        <input
                                                            className="form-control-modern p-3 rounded-4 w-100"
                                                            placeholder={t('subcontracting.modal.cityPlaceholder')}
                                                            value={newSubForm.city}
                                                            onChange={e => setNewSubForm({ ...newSubForm, city: e.target.value })}
                                                        />
                                                    </div>
                                                </div>

                                                <div className="col-12 mt-2">
                                                    <button
                                                        type="submit"
                                                        className="btn btn-success btn-lg w-100 rounded-4 py-3 mt-3 shadow-lg hover-scale transition-all d-flex align-items-center justify-content-center gap-2 fw-bold"
                                                        disabled={loading}
                                                    >
                                                        {loading ? (
                                                            <>
                                                                <span className="spinner-border spinner-border-sm" />
                                                                {t('subcontracting.modal.creating')}
                                                            </>
                                                        ) : (
                                                            <>
                                                                <Check size={20} />
                                                                {t('subcontracting.modal.createButton')}
                                                            </>
                                                        )}
                                                    </button>
                                                </div>
                                            </form>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                .hover-lift:hover { transform: translateY(-0.3125rem); box-shadow: 0 0.625rem 1.25rem rgba(0,0,0,0.1) !important; }
                .hover-bg-light:hover { background-color: #f8f9fa !important; }
                .transition-all { transition: all 0.25s ease; }
                .btn-white { background: white; }

                /* NEW PREMIUM MODAL STYLES */
                @keyframes modalFadeIn {
                    from { opacity: 0; transform: translateY(20px) scale(0.98); }
                    to { opacity: 1; transform: translateY(0) scale(1); }
                }
                
                .shadow-2xl {
                    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
                }

                .fw-800 { font-weight: 800; }
                
                .hover-rotate:hover {
                    transform: rotate(90deg);
                    background-color: #fee2e2 !important;
                    color: #ef4444 !important;
                }

                .group:hover .group-hover-bg-primary {
                    background-color: var(--bs-primary) !important;
                    color: white !important;
                }

                .hover-shadow:hover {
                    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
                    border-color: var(--bs-primary) !important;
                }

                .group-focus-within:focus-within {
                    border-color: var(--bs-primary) !important;
                    box-shadow: 0 0 0 4px rgba(52, 152, 219, 0.15) !important;
                }

                .form-control-modern {
                    background-color: white;
                    border: 1.5px solid #edf2f7;
                    transition: all 0.2s ease;
                    font-size: 0.95rem;
                }

                .form-control-modern:focus {
                    outline: none;
                    border-color: #198754;
                    background-color: #fff;
                    box-shadow: 0 0 0 4px rgba(25, 135, 84, 0.1);
                }

                .label-modern {
                    font-size: 0.8rem;
                    font-weight: 700;
                    color: #4a5568;
                    display: flex;
                    align-items: center;
                    text-transform: uppercase;
                    letter-spacing: 0.025em;
                }

                .hover-scale:hover:not(:disabled) {
                    transform: translateY(-2px);
                    box-shadow: 0 10px 20px -5px rgba(25, 135, 84, 0.4) !important;
                }

                .custom-scrollbar::-webkit-scrollbar {
                    width: 6px;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                    background: #f1f1f1;
                    border-radius: 10px;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: #cbd5e0;
                    border-radius: 10px;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                    background: #a0aec0;
                }
            `}</style>
        </DashboardLayout>
    );
};

export default Subcontractors;
